import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useStyles } from '../DataTable.styles';
import type { DataTableCellValue, FilterOption } from '../DataTable.types';
import { i18n } from './FilterCheckboxList.i18n';

export interface FilterCheckboxListProps {
    field: string;
    options: FilterOption[];
    selected: DataTableCellValue[] | undefined;
    onChange: (field: string, values: DataTableCellValue[]) => void;
}

const isChecked = (
    selected: DataTableCellValue[] | undefined,
    options: FilterOption[],
    value: DataTableCellValue,
): boolean => {
    if (selected === undefined) return options.some((o) => o.value === value);
    return selected.includes(value);
};

export const FilterCheckboxList = ({
    field,
    options,
    selected,
    onChange,
}: FilterCheckboxListProps) => {
    const intl = useIntl();
    const { classes } = useStyles();

    const allValues = useMemo(() => options.map((o) => o.value), [options]);
    const effectiveCount = selected === undefined ? options.length : selected.length;
    const allSelected = effectiveCount === options.length;

    const handleToggleAll = useCallback(() => {
        // "Select all" is one-way — unchecking would leave zero options, which is disallowed.
        if (allSelected) return;
        onChange(field, allValues);
    }, [allSelected, allValues, field, onChange]);

    const handleToggleOption = useCallback(
        (value: DataTableCellValue) => {
            const current = selected === undefined ? allValues.slice() : selected.slice();
            const idx = current.indexOf(value);
            if (idx >= 0) {
                if (current.length <= 1) return;
                current.splice(idx, 1);
            } else {
                current.push(value);
            }
            onChange(field, current);
        },
        [selected, allValues, field, onChange],
    );

    return (
        <div className={classes.filterPopoverContent}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={allSelected}
                        onChange={handleToggleAll}
                        size="small"
                        automation-id={`filter-select-all-${field}`}
                    />
                }
                label={intl.formatMessage(i18n.selectAll)}
                className={classes.filterSelectAll}
            />
            {options.map((opt) => (
                <FormControlLabel
                    key={String(opt.value)}
                    control={
                        <Checkbox
                            checked={isChecked(selected, options, opt.value)}
                            onChange={() => handleToggleOption(opt.value)}
                            size="small"
                            automation-id={`filter-option-${field}-${String(opt.value)}`}
                        />
                    }
                    label={opt.label}
                />
            ))}
        </div>
    );
};
